import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { formatDealCustomText } from '@/features/rapidstor/helpers';
import { useDealDisplay } from '@/journey-based-listing/hooks/useDealDisplay';

import { DealSelectorProps } from '../OptionSelector';

import classes from './DealDrawer.module.scss';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
}

export default function DealDrawer({
    deals,
    noDealOption,
    onSelectDeal,
    onClearDeal,
    location,
}: DealSelectorProps) {
    const { t } = useTranslation();
    const { getDealPriceDisplay } = useDealDisplay();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);

    const activeDeal = deals.find((d) => d.isActive);
    const hasNoDealActive = noDealOption.isActive;

    const handleSelectDeal = useCallback(
        (dealId: string) => {
            onSelectDeal(dealId);
            setTimeout(() => setIsOpen(false), 200);
        },
        [onSelectDeal],
    );

    const handleClearDeal = useCallback(() => {
        onClearDeal();
        setTimeout(() => setIsOpen(false), 200);
    }, [onClearDeal]);

    const handleBackdropClick = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Banner display values
    let bannerTitle: string | null = null;
    let bannerRate: string | null = null;

    if (activeDeal) {
        bannerTitle = activeDeal.deal.title;
        bannerRate = formatPriceLabel(formatCurrency(activeDeal.rate, location), location);
    } else if (hasNoDealActive && noDealOption.show) {
        bannerTitle = t('deal_drawer:standard_rate', 'Standard Rate');
        bannerRate = formatPriceLabel(formatCurrency(noDealOption.rate, location), location);
    }

    const drawerContent = (
        <>
            <span className={classes.drawerTitle}>
                {t('deal_drawer:choose_deal', 'Choose your deal')}
            </span>

            <div className={classes.dealCardList}>
                {noDealOption.show && (
                    <div
                        className={`${classes.dealCard} ${hasNoDealActive ? classes.dealCardActive : ''}`}
                        onClick={handleClearDeal}
                    >
                        <div className={classes.dealCardHeader}>
                            <div className={classes.dealCardInfo}>
                                <div className={classes.dealCardTitle}>
                                    {t('deal_drawer:standard_rate', 'Standard Rate')}
                                </div>
                            </div>
                            <span className={classes.dealCardRate}>
                                {formatPriceLabel(formatCurrency(noDealOption.rate, location), location)}
                            </span>
                        </div>
                        {hasNoDealActive && (
                            <svg
                                className={classes.checkmark}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        )}
                    </div>
                )}

                {deals.map(({ deal, rate, isActive }) => (
                    <div
                        key={`${deal._id}`}
                        className={`${classes.dealCard} ${isActive ? classes.dealCardActive : ''}`}
                        onClick={() => handleSelectDeal(`${deal._id}`)}
                    >
                        <div className={classes.dealCardHeader}>
                            <div className={classes.dealCardInfo}>
                                <div className={classes.dealCardTitle}>
                                    {deal.title}
                                </div>
                                {deal.customFormat?.primary && deal.customFormat.primary !== '' && (
                                    <div className={classes.dealCardCustomText}>
                                        <span>
                                            {formatDealCustomText(
                                                deal,
                                                location,
                                                deal.customFormat.primary,
                                            )}
                                        </span>
                                        {deal.customFormat.secondary && deal.customFormat.secondary !== '' && (
                                            <span>
                                                {formatDealCustomText(
                                                    deal,
                                                    location,
                                                    deal.customFormat.secondary,
                                                )}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <span className={classes.dealCardRate}>
                                {getDealPriceDisplay(deal, rate)}
                            </span>
                        </div>

                        {deal.descriptions && deal.descriptions.length > 0 && (
                            <div className={classes.dealCardDescriptions}>
                                {deal.descriptions.map(
                                    (description: string, index: number) => (
                                        <span
                                            key={`${index}_${deal._id}_desc`}
                                            className={classes.descriptionTag}
                                        >
                                            {description}
                                        </span>
                                    ),
                                )}
                            </div>
                        )}

                        {isActive && (
                            <svg
                                className={classes.checkmark}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        )}
                    </div>
                ))}
            </div>
        </>
    );

    const overlay = (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className={classes.backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={handleBackdropClick}
                    />

                    {isMobile ? (
                        <motion.div
                            className={classes.bottomSheet}
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{
                                type: 'spring',
                                damping: 30,
                                stiffness: 300,
                            }}
                        >
                            <div className={classes.dragHandle} />
                            {drawerContent}
                        </motion.div>
                    ) : (
                        <motion.div
                            className={classes.sidePanel}
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{
                                type: 'spring',
                                damping: 30,
                                stiffness: 300,
                            }}
                        >
                            <button
                                type="button"
                                className={classes.closeButton}
                                onClick={() => setIsOpen(false)}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                            {drawerContent}
                        </motion.div>
                    )}
                </>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <div
                className={`${classes.banner} ${!bannerTitle ? classes.bannerEmpty : ''}`}
                onClick={() => setIsOpen(true)}
            >
                <div className={classes.bannerLeft}>
                    <span className={classes.bannerTitle}>
                        {bannerTitle || t('deal_drawer:select_deal', 'Select a deal')}
                    </span>
                    {bannerRate && (
                        <span className={classes.bannerRate}>{bannerRate}</span>
                    )}
                </div>
                <div className={classes.bannerRight}>
                    <span className={classes.changeDealText}>
                        {bannerTitle
                            ? t('deal_drawer:change_deal', 'Change deal')
                            : ''}
                    </span>
                    <svg
                        className={classes.chevron}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </div>

            {createPortal(overlay, document.body)}
        </>
    );
}
